import Image from 'next/legacy/image';
import { useTranslation } from 'next-i18next';
import { CacheStorageMedia } from '@sy/ssr-types';

import { getTagTranslationKey } from '~/services/caseStudy/caseStudyTags';
import {
	Content,
	HookContent,
	HookInfoColumn,
	HookWrapper,
	ImageWrapper,
	LogoWrapper,
	Tag,
	TagList,
} from './CaseStudyHook.styles';
import VideoEmbed, { isValidVideoUrl } from './VideoEmbed';

const LOGO_HEIGHT = 73;
const IMAGE_WIDTH = 640;

const CaseStudyHook = ({
	logo,
	image,
	content,
	tags,
	videoUrl,
}: CaseStudyHookProps) => {
	const { t } = useTranslation('caseStudies');
	const tagTranslationKeys =
		tags?.map(tag => getTagTranslationKey(tag)).filter(Boolean) || [];
	const showVideo = isValidVideoUrl(videoUrl);

	return (
		<HookWrapper>
			<HookContent>
				<HookInfoColumn>
					{logo.url && logo.dimensions && logo.dimensions.height !== 0 && (
						<LogoWrapper>
							<Image
								src={logo.url}
								alt={logo.description}
								height={LOGO_HEIGHT}
								width={
									(LOGO_HEIGHT * logo.dimensions.width) / logo.dimensions.height
								}
							/>
						</LogoWrapper>
					)}
					<Content>{content}</Content>
					{tagTranslationKeys.length > 0 && (
						<TagList>
							{tagTranslationKeys.map(translationKey => (
								<Tag key={translationKey}>{t(translationKey)}</Tag>
							))}
						</TagList>
					)}
				</HookInfoColumn>
				<HookInfoColumn>
					{showVideo && <VideoEmbed videoUrl={videoUrl!} />}
					{!showVideo &&
						image &&
						image.url &&
						image.dimensions &&
						image.dimensions.width !== 0 && (
							<ImageWrapper>
								<Image
									src={image.url}
									alt={image.description}
									height={
										(IMAGE_WIDTH * image.dimensions.height) /
										image.dimensions.width
									}
									width={IMAGE_WIDTH}
								/>
							</ImageWrapper>
						)}
				</HookInfoColumn>
			</HookContent>
		</HookWrapper>
	);
};

export type CaseStudyHookProps = {
	logo: CacheStorageMedia;
	image?: CacheStorageMedia;
	content: string;
	tags?: string[];
	videoUrl?: string;
};

export default CaseStudyHook;
